import { onUnmounted, watch } from 'vue';
import {
  useMapArea,
  usePointerStateManagement,
  usePointerState,
  PointerInteractionService,
  getPointerPosition,
  calculateMovement,
  isEditingText,
  blurActiveEditable
} from '@/modules/map-editor';
import type {
  PointerEventContext,
  HandleMoveParams,
  HandleDownParams,
  HandleUpParams
} from '@/modules/map-editor';

/**
 * Composable for handling pointer events.
 * Translates raw DOM events into typed params and delegates to PointerInteractionService.
 * All store-related state and callbacks are owned by usePointerStateManagement.
 */
export function usePointerEvents() {
  const { mapAreaService } = useMapArea();

  const pointerState = usePointerStateManagement();

  const {
    state,
    computedCursor,
    updatePosition,
    setHoveredNodeId,
    setHoveredEdgeId,
    setHoveredSizerPoint,
    setShiftPressed,
    setSpacePressed,
    reset
  } = usePointerState();

  const interactionService = new PointerInteractionService(
    state.value,
    pointerState.nodes.value,
    pointerState.edges.value
  );

  watch(
    () => [pointerState.nodes.value, pointerState.edges.value],
    ([newNodes, newEdges]) => {
      interactionService.updateElements(newNodes || [], newEdges || []);
    },
    { deep: false }
  );

  // --- Shared param builders ---------------------------------------------------

  function buildMoveParams(position: { x: number; y: number }, context: PointerEventContext): HandleMoveParams {
    return {
      ctx: context.contexts.ctx!,
      position,
      zoomLevel: context.zoomLevel,
      isShiftPressed: state.value.isShiftPressed,
      canvasWidth: context.contexts.ctx!.canvas.width,
      canvasHeight: context.contexts.ctx!.canvas.height,
      nodes: pointerState.nodes.value,
      edges: pointerState.edges.value,
      mapAreaService: mapAreaService,
      sendPointerState: pointerState.sendPointerStateWithThrottle,
      userCurrent: pointerState.userCurrent.value,
      selectedNode: pointerState.selectedNode.value,
      updateNodeDesign: pointerState.updateNodeDesign,
      selectedNodes: pointerState.selectedNodes.value
    };
  }

  function syncHoverStateFromService(): void {
    const serviceState = interactionService.getState();
    setHoveredNodeId(serviceState.hoveredNodeId);
    setHoveredEdgeId(serviceState.hoveredEdgeId);
    if (serviceState.hoveredSizerPoint) {
      setHoveredSizerPoint(serviceState.hoveredSizerPoint);
      pointerState.nodeSizerPointHovered.value = serviceState.hoveredSizerPoint;
    } else if (pointerState.nodeSizerPointHovered.value) {
      setHoveredSizerPoint(null);
      pointerState.nodeSizerPointHovered.value = null;
    }
  }

  function buildUpParams(event: MouseEvent | TouchEvent, context: PointerEventContext): HandleUpParams {
    return {
      nodes: pointerState.nodes.value,
      edges: pointerState.edges.value,
      currentLayerId: pointerState.currentLayerId.value,
      mapAreaService: mapAreaService,
      saveNodePosition: pointerState.saveNodePosition,
      saveNodeDesign: pointerState.saveNodeDesign,
      setSelectedNode: pointerState.setSelectedNode,
      setSelectedEdge: pointerState.setSelectedEdge,
      toggleNodeOptions: pointerState.toggleNodeOptions,
      toggleEdgeOptions: pointerState.toggleEdgeOptions,
      isReadOnly: pointerState.isUserAccessLevelRead.value,
      hasPermission: pointerState.hasUserPermission.value,
      isQuickEditBarContentTracked: pointerState.isQuickEditBarContentTracked.value,
      updateQuickEditBarContentWarning: pointerState.updateQuickEditBarContentWarning,
      isNodeDrawingMode: pointerState.isNodeDrawingMode.value,
      isEdgeConnectionsDrawingMode: pointerState.isEdgeConnectionsDrawingMode.value,
      edgeConnections: pointerState.edgeConnections.value,
      createNode: pointerState.createNode,
      connectEdge: pointerState.connectEdge,
      createEdge: pointerState.createEdge,
      markNodeConnect: pointerState.markNodeConnect,
      selectedNode: pointerState.selectedNode.value,
      selectedEdge: pointerState.selectedEdge.value,
      event: event,
      ctx: context.contexts.ctx!,
      zoomLevel: context.zoomLevel,
      mapArea: mapAreaService.getState(),
      editedNode: pointerState.editedNode.value,
      editedEdge: pointerState.editedEdge.value,
      stopEditingNode: pointerState.stopEditingNode,
      stopEditingEdge: pointerState.stopEditingEdge,
      isNodeDesignSettingsOpened: pointerState.isNodeDesignSettingsOpened.value,
      isEdgeDesignSettingsOpened: pointerState.isEdgeDesignSettingsOpened.value,
      sectionActive: pointerState.sectionActive.value,
      chooseOption: pointerState.chooseOption,
      toggleSection: pointerState.toggleSection,
      closeSidebar: pointerState.closeSidebar,
      cancelEdgeConnection: pointerState.cancelEdgeConnection,
      adjustMapForQuickEditBar: pointerState.adjustMapForQuickEditBar,
      updateNodeDesign: pointerState.updateNodeDesign,
      isMapTransfer: pointerState.isMapTransfer.value,
      isShiftPressed: state.value.isShiftPressed,
      selectNode: pointerState.selectNode,
      deselectAllNodes: pointerState.deselectAllNodes,
      selectMultipleNodes: pointerState.selectMultipleNodes,
      selectedCount: pointerState.selectedCount.value,
    };
  }

  function resetAfterUp(): void {
    updatePosition({ x: 0, y: 0, clientX: 0, clientY: 0, movementX: 0, movementY: 0 });
    pointerState.isNodeSizing.value = false;
    pointerState.nodeDimensions.value = {};
    pointerState.nodeSizerPointHovered.value = null;
  }

  // --- Double-tap detection for touch devices ----------------------------------

  let lastTapTime = 0;
  const DOUBLE_TAP_THRESHOLD = 300;

  // --- Unified pointer handlers ------------------------------------------------

  function handleMove(event: MouseEvent | TouchEvent, context: PointerEventContext): void {
    event.preventDefault();
    event.stopPropagation();

    const position = getPointerPosition(event, context.canvas);
    const movement = calculateMovement(position, state.value.position);
    updatePosition({ ...position, ...movement });

    interactionService.handleMove(buildMoveParams({ x: position.x, y: position.y }, context));
    syncHoverStateFromService();
  }

  function handleDown(event: MouseEvent | TouchEvent, context: PointerEventContext): void {
    event.preventDefault();
    event.stopPropagation();
    // preventDefault above suppresses the implicit blur, so a focused field
    // (e.g. the node title) would keep focus and swallow canvas shortcuts such
    // as space-to-pan. Drop focus explicitly when interacting with the canvas.
    blurActiveEditable();

    const position = getPointerPosition(event, context.canvas);
    updatePosition({ x: position.x, y: position.y, clientX: position.clientX, clientY: position.clientY, movementX: 0, movementY: 0 });

    const downParams: HandleDownParams = {
      ctx: context.contexts.ctx!,
      position: { x: position.x, y: position.y },
      zoomLevel: context.zoomLevel,
      canvasWidth: context.contexts.ctx!.canvas.width,
      canvasHeight: context.contexts.ctx!.canvas.height,
      nodes: pointerState.nodes.value,
      mapAreaService: mapAreaService,
      isReadOnly: pointerState.isUserAccessLevelRead.value,
      hasPermission: pointerState.hasUserPermission.value,
      selectedNode: pointerState.selectedNode.value,
      nodeSizerPointHovered: pointerState.nodeSizerPointHovered.value,
      saveNodePosition: pointerState.saveNodePosition,
      selectedNodes: pointerState.selectedNodes.value
    };

    interactionService.handleDown(downParams);
  }

  function handleUp(event: MouseEvent | TouchEvent, context: PointerEventContext): void {
    event.preventDefault();
    event.stopPropagation();
    interactionService.handleUp(buildUpParams(event, context));
    resetAfterUp();

    if ('changedTouches' in event) {
      const now = Date.now();
      if (now - lastTapTime < DOUBLE_TAP_THRESHOLD) {
        lastTapTime = 0;
        handleDoubleClick(event, context);
      } else {
        lastTapTime = now;
      }
    }
  }

  // --- Other handlers ----------------------------------------------------------

  function handleDoubleClick(event: MouseEvent | TouchEvent, context: PointerEventContext): void {
    event.preventDefault();
    event.stopPropagation();

    if (state.value.hoveredNodeId === -1 && state.value.hoveredEdgeId === -1) {
      pointerState.createNode(event, context.contexts.ctx!, context.zoomLevel, mapAreaService.getState());
    }
  }

  function handleContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Shift') {
      setShiftPressed(true);
    }
    // Space → hand (pan) tool. Skip while typing in a field, and stop the
    // default page scroll so holding space only pans the canvas.
    if ((event.code === 'Space' || event.key === ' ') && !isEditingText()) {
      event.preventDefault();
      setSpacePressed(true);
    }
  }

  function handleKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Shift') {
      setShiftPressed(false);
    }
    if (event.code === 'Space' || event.key === ' ') {
      setSpacePressed(false);
    }
  }

  onUnmounted(() => {
    reset();
  });

  return {
    computedCursor,
    pointerState: state,
    handleMove,
    handleDown,
    handleUp,
    handleDoubleClick,
    handleContextMenu,
    handleKeyDown,
    handleKeyUp
  };
}
